import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import AuthField from './AuthField';
import ImageCaptchaField from './ImageCaptchaField';
import SliderCaptcha from './SliderCaptcha';
import TurnstileWidget from './TurnstileWidget';
import { useTranslation } from '../i18n/LocaleContext';

const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || '';

const Login = ({ onLogin, onSwitchToRegister }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaRefresh, setCaptchaRefresh] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState(null);
  // token 一次性:登录失败后自增 key 重挂挂件,让用户重新验证
  const [turnstileKey, setTurnstileKey] = useState(0);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!captchaCode) {
      setError(t('imageCaptchaRequired'));
      setCaptchaRefresh((k) => k + 1);
      return;
    }
    if (TURNSTILE_SITEKEY ? !turnstileToken : !captchaOk) {
      setError(t('captchaRequired'));
      return;
    }
    setLoading(true);
    try {
      await onLogin({ ...formData, captchaId, captchaCode, turnstileToken });
    } catch (err) {
      const apiError = err.response?.data?.error || err.message;
      setError(apiError || t('loginFailed'));
      setCaptchaRefresh((k) => k + 1);
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {error && (
        <p className="rounded-xl bg-red-50 px-3.5 py-2.5 text-[12.5px] font-medium text-red-600">{error}</p>
      )}

      {/* Reference-style auth: plain pill inputs, no grouping chrome */}
      <AuthField
        pill
        type="text"
        name="identifier"
        value={formData.identifier}
        onChange={handleChange}
        required
        autoComplete="username"
        placeholder={t('identifier')}
      />

      <AuthField
        pill
        type="password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        required
        autoComplete="current-password"
        placeholder={t('password')}
      />

      <ImageCaptchaField
        refreshSignal={captchaRefresh}
        onChange={(code, id) => { setCaptchaCode(code); setCaptchaId(id); }}
      />

      {TURNSTILE_SITEKEY ? (
        <TurnstileWidget key={turnstileKey} onToken={setTurnstileToken} />
      ) : (
        <SliderCaptcha onVerified={setCaptchaOk} />
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full h-[52px] mt-1 flex items-center justify-center gap-2 rounded-xl
          bg-brand-500
          text-[15px] font-bold text-white disabled:opacity-55"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {loading ? t('processing') : t('login')}
      </button>

      <p className="pt-1 text-center text-[12.5px] text-ink-muted">
        {t('noAccount')}{' '}
        <button type="button" onClick={onSwitchToRegister} className="font-bold text-brand-600">
          {t('register')}
        </button>
      </p>
    </form>
  );
};

export default Login;
